import { Group } from 'three';
import { Cut } from '../extras/cut.model';
import { PhoenixObjects } from './objects/phoenix-objects';
/**
 * Loader for processing and loading an event.
 */
var PhoenixLoader = /** @class */ (function () {
    function PhoenixLoader() {
    }
    /**
     * Takes an object that represents ONE event and takes care of adding
     * the different objects to the graphic library and the UI controls.
     * @param eventData Object representing the event.
     * @param graphicsLibrary Service containing functionality to draw the 3D objects.
     * @param ui Service for showing menus and controls to manipulate the geometries.
     * @param infoLogger Service for logging data to the information panel.
     */
    PhoenixLoader.prototype.buildEventData = function (eventData, graphicsLibrary, ui, infoLogger) {
        this.graphicsLibrary = graphicsLibrary;
        this.ui = ui;
        this.eventData = eventData;
        // Replacing tracks with tracks through Runge-Kutta
        // TODO - make this configurable? Or possibly automatic if tracks have <2 positions to draw?
        // Object.assign(this.eventData.Tracks, this.getTracksWithRungeKutta(this.eventData['Tracks']));
        // initiate load
        this.loadObjectTypes(eventData);
        var eventNumber = eventData['event number'] ? eventData['event number'] : eventData['eventNumber'];
        var runNumber = eventData['run number'] ? eventData['run number'] : eventData['runNumber'];
        infoLogger.add('Event#' + eventNumber + ' from run#' + runNumber, 'Loaded');
    };
    /**
     * Get the list of event names from the event data.
     * @param eventsData Object containing all event data.
     * @returns List of event names.
     */
    PhoenixLoader.prototype.getEventsList = function (eventsData) {
        var eventsList = [];
        for (var _i = 0, _a = Object.keys(eventsData); _i < _a.length; _i++) {
            var eventName = _a[_i];
            if (eventsData[eventName] !== null) {
                eventsList.push(eventName);
            }
        }
        return eventsList;
    };
    /**
     * Get list of collections in the event data.
     * @returns List of all collection names.
     */
    PhoenixLoader.prototype.getCollections = function () {
        if (!this.eventData) {
            return null;
        }
        var collections = [];
        for (var _i = 0, _a = Object.keys(this.eventData); _i < _a.length; _i++) {
            var objectType = _a[_i];
            if (this.eventData[objectType] && typeof this.eventData[objectType] === 'object') {
                for (var _b = 0, _c = Object.keys(this.eventData[objectType]); _b < _c.length; _b++) {
                    var collection = _c[_b];
                    collections.push(collection);
                }
            }
        }
        return collections;
    };
    /**
     * Get the collection with the given collection name from the event data.
     * @param collectionName Name of the collection to get.
     * @returns An object containing the collection.
     */
    PhoenixLoader.prototype.getCollection = function (collectionName) {
        if (!this.eventData) {
            return null;
        }
        for (var _i = 0, _a = Object.keys(this.eventData); _i < _a.length; _i++) {
            var objectType = _a[_i];
            if (this.eventData[objectType]) {
                for (var _b = 0, _c = Object.keys(this.eventData[objectType]); _b < _c.length; _b++) {
                    var collection = _c[_b];
                    if (collection === collectionName) {
                        return this.eventData[objectType][collection];
                    }
                }
            }
        }
    };
    /**
     * Receives an object containing the data from an event and parses it
     * to reconstruct the different collections of physics objects.
     * @param eventData Representing ONE event (expressed in the Phoenix format).
     */
    PhoenixLoader.prototype.loadObjectTypes = function (eventData) {
        var _this = this;
        if (eventData.Tracks) {
            // (Optional) Cuts can be added to any physics object.
            var cuts = [
                new Cut('chi2', 0, 50),
                new Cut('dof', 0, 100),
                new Cut('mom', 0, 500)
            ];
            this.addObjectType(eventData.Tracks, PhoenixObjects.getTrack, 'Tracks', cuts);
            // infoLogger.add('Got ' + Object.keys(eventData.Tracks).length + ' Track collections.');
        }
        if (eventData.Jets) {
            // (Optional) Cuts can be added to any physics object.
            var cuts = [
                new Cut('phi', -Math.PI, Math.PI),
                new Cut('eta', 0, 100),
                new Cut('energy', 2000, 10000)
            ];
            var addJetsSizeOption = function (typeFolder, typeFolderPM) {
                if (typeFolder) {
                    var sizeMenu = typeFolder.add({ jetsScale: 100 }, 'jetsScale', 1, 200)
                        .name('Jets Size (%)');
                    sizeMenu.onChange(function (value) {
                        _this.graphicsLibrary.getSceneManager().scaleJets(value);
                    });
                }
                // Phoenix menu
                if (typeFolderPM) {
                    typeFolderPM.addConfig('slider', {
                        label: 'Jets Size (%)',
                        min: 1, max: 200,
                        allowCustomValue: true,
                        onChange: function (value) {
                            _this.graphicsLibrary.getSceneManager().scaleJets(value);
                        }
                    });
                }
            };
            this.addObjectType(eventData.Jets, PhoenixObjects.getJet, 'Jets', cuts, addJetsSizeOption);
        }
        if (eventData.Hits) {
            this.addObjectType(eventData.Hits, PhoenixObjects.getHits, 'Hits');
        }
        if (eventData.CaloClusters) {
            // (Optional) Cuts can be added to any physics object.
            var cuts = [
                new Cut('phi', -Math.PI, Math.PI),
                new Cut('eta', 0, 100),
                new Cut('energy', 2000, 10000)
            ];
            this.addObjectType(eventData.CaloClusters, PhoenixObjects.getCluster, 'CaloClusters', cuts);
        }
        if (eventData.Muons) {
            this.addObjectType(eventData.Muons, this.getMuon, 'Muons');
        }
    };
    /**
     * Adds to the event display all collections of a given object type.
     * @param object Contains all collections of a given type (Tracks, Jets, CaloClusters...).
     * @param getObject Function that handles of reconstructing objects of the given type.
     * @param typeName Label for naming the object type.
     * @param cuts Filters that can be applied to the objects.
     * @param extendEventDataTypeUI A callback to add more options to event data type UI folder.
     */
    PhoenixLoader.prototype.addObjectType = function (object, getObject, typeName, cuts, extendEventDataTypeUI) {
        var typeFolder = this.ui.addEventDataTypeFolder(typeName, extendEventDataTypeUI);
        var typeFolderPM = this.ui.addEventDataTypeFolderPM(typeName, extendEventDataTypeUI);
        var objectGroup = this.graphicsLibrary.addEventDataTypeGroup(typeName);
        var collectionsList = this.getObjectTypeCollections(object);
        var _loop_1 = function (collectionName) {
            var objectCollection = object[collectionName];
            // console.log(typeName+" collection "+collectionName+" has "+objectCollection.length+" constituents.")
            this_1.addCollection(objectCollection, collectionName, getObject, objectGroup);
            cuts = cuts === null || cuts === void 0 ? void 0 : cuts.filter(function (cut) { return objectCollection[0][cut.field]; });
            this_1.ui.addCollection(typeFolder, collectionName, cuts);
            this_1.ui.addCollectionPM(typeFolderPM, collectionName, cuts);
        };
        var this_1 = this;
        for (var _i = 0, collectionsList_1 = collectionsList; _i < collectionsList_1.length; _i++) {
            var collectionName = collectionsList_1[_i];
            _loop_1(collectionName);
        }
    };
    /**
     * Adds to the event display all the objects inside a collection.
     * @param objectCollection Contains the params for every object of the collection.
     * @param collectionName Label to UNIQUELY identify the collection.
     * @param getObject Handles reconstructing the objects of the collection.
     * @param objectGroup Group containing the collections of the same object type.
     */
    PhoenixLoader.prototype.addCollection = function (objectCollection, collectionName, getObject, objectGroup) {
        var collscene = new Group();
        collscene.name = collectionName;
        for (var _i = 0, objectCollection_1 = objectCollection; _i < objectCollection_1.length; _i++) {
            var objectParams = objectCollection_1[_i];
            var object = getObject.bind(this)(objectParams);
            if (object) {
                collscene.add(object);
            }
        }
        objectGroup.add(collscene);
        // console.log("-> Adding a threejs group called "+collscene.name+" with "+collscene.children.length+" children to the group called "+objectGroup.name);
    };
    /**
     * Get collection names of a given object type.
     * @param object Contains all collections of a given type (Tracks, Jets, CaloClusters etc.).
     * @returns List of collection names of an object type (Tracks, Jets, CaloClusters etc.).
     */
    PhoenixLoader.prototype.getObjectTypeCollections = function (object) {
        var collectionsList = [];
        for (var _i = 0, _a = Object.keys(object); _i < _a.length; _i++) {
            var collectionName = _a[_i];
            if (object[collectionName] !== null) {
                collectionsList.push(collectionName);
            }
        }
        return collectionsList;
    };
    /**
     * Process the Muon from the given parameters and get it as a group.
     * @param muonParams Parameters of the Muon.
     * @returns Muon group containing Clusters and Tracks.
     */
    PhoenixLoader.prototype.getMuon = function (muonParams) {
        var muonScene = new Group();
        for (var _i = 0, _a = muonParams.LinkedClusters; _i < _a.length; _i++) {
            var clusterID = _a[_i];
            var clusterColl = clusterID.split(':')[0];
            var clusterIndex = clusterID.split(':')[1];
            if (clusterColl && clusterIndex && this.eventData.CaloClusters && this.eventData.CaloClusters[clusterColl]) {
                var clusterParams = this.eventData.CaloClusters[clusterColl][clusterIndex];
                if (clusterParams) {
                    var cluster = PhoenixObjects.getCluster(clusterParams);
                    muonScene.add(cluster);
                }
            }
        }
        for (var _b = 0, _c = muonParams.LinkedTracks; _b < _c.length; _b++) {
            var trackID = _c[_b];
            var trackColl = trackID.split(':')[0];
            var trackIndex = trackID.split(':')[1];
            if (trackColl && trackIndex && this.eventData.Tracks && this.eventData.Tracks[trackColl]) {
                var trackParams = this.eventData.Tracks[trackColl][trackIndex];
                if (trackParams) {
                    var track = PhoenixObjects.getTrack(trackParams);
                    muonScene.add(track);
                }
            }
        }
        // uuid for selection of muons from the collections info panel
        muonParams.uuid = muonScene.uuid;
        muonScene.name = 'Muon';
        // add to scene
        return muonScene;
    };
    /**
     * Get metadata associated to the event (experiment info, time, run, event...).
     * @returns Metadata of the event.
     */
    PhoenixLoader.prototype.getEventMetadata = function () {
        var metadata = [];
        // Dividing event meta data into groups by keys and label
        // For example, the first array group is for "Run / Event / LS"
        var eventDataPropGroups = [
            [
                { keys: ['runNumber', 'run number'], label: 'Run' },
                { keys: ['eventNumber', 'event number'], label: 'Event' },
                { keys: ['ls'], label: 'LS' },
                { keys: ['lumiBlock'], label: 'LumiBlock' }
            ],
            [
                { keys: ['time'], label: 'Data recorded' }
            ]
        ];
        var eventDataKeys = Object.keys(this.eventData);
        // Iterating the group
        for (var _i = 0, eventDataPropGroups_1 = eventDataPropGroups; _i < eventDataPropGroups_1.length; _i++) {
            var eventDataPropGroup = eventDataPropGroups_1[_i];
            var combinedProps = {};
            // Iterating the props inside a group
            for (var _a = 0, eventDataPropGroup_1 = eventDataPropGroup; _a < eventDataPropGroup_1.length; _a++) {
                var eventDataProp = eventDataPropGroup_1[_a];
                // Iterating each possible key of a prop
                for (var _b = 0, _c = eventDataProp.keys; _b < _c.length; _b++) {
                    var eventDataPropKey = _c[_b];
                    if (eventDataKeys.includes(eventDataPropKey) && this.eventData[eventDataPropKey]) {
                        combinedProps[eventDataProp.label] = this.eventData[eventDataPropKey];
                        break;
                    }
                }
            }
            if (Object.keys(combinedProps).length > 0) {
                // Joining and pushing the collected combined properties to the actual metadata
                metadata.push({
                    label: Object.keys(combinedProps).join(' / '),
                    value: Object.values(combinedProps).join(' / ')
                });
            }
        }
        return metadata;
    };
    return PhoenixLoader;
}());
export { PhoenixLoader };
//# sourceMappingURL=phoenix-loader.js.map